import { useState } from 'react';
import { rotatedBounds, unionRects, type Rect } from '../../doc/geometry';
import { PanelFrame } from '../floating';
import { useBoard, useBoardMeta, usePane, usePaneStore } from '../context';
import { exportBoardJson, exportBoardZip } from './backup';
import { boardBounds } from './rasterize';
import { exportRect } from './exportActions';
import { useExportSettings, setExportSettings } from './exportSettings';

/** Export dialog: PNG of the selection, the board or a dragged area; and a board backup (ZIP with JSON + images, or JSON only). */
export function ExportPanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const meta = useBoardMeta();
  const selection = usePane((s) => s.selection);
  const settings = useExportSettings((s) => s);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [backupInfo, setBackupInfo] = useState<string | null>(null);

  const run = async (rect: Rect | null, label: string) => {
    if (!rect) return;
    setBusy('Rendering…');
    setError(null);
    try {
      await exportRect(board, meta.title, rect, label);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const runZip = async () => {
    setBusy('Collecting…');
    setError(null);
    setBackupInfo(null);
    try {
      const r = await exportBoardZip(board, meta.title, (done, total) => setBusy(total ? `Packing files ${done}/${total}…` : 'Packing…'));
      setBackupInfo(`ZIP saved: ${r.assets} ${r.assets === 1 ? 'file' : 'files'} packed${r.missing.length ? `, ${r.missing.length} missing (listed in board.json)` : ''}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const runJson = async () => {
    setBusy('Writing JSON…');
    setError(null);
    setBackupInfo(null);
    try {
      await exportBoardJson(board, meta.title);
      setBackupInfo('JSON saved (no image files).');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const selectionRect = () => {
    const items = [...selection].map((id) => board.index.get(id)).filter((i): i is NonNullable<typeof i> => !!i);
    return unionRects(items.map(rotatedBounds));
  };

  return (
    <PanelFrame kind="export" title="Export" className="export-panel" onClose={onClose}>
      <div className="insp-body">
        <div className="insp-label">PNG scale</div>
        <div className="btn-row">
          {[1, 2, 4].map((s) => (
            <button key={s} type="button" className={`btn${settings.scale === s ? ' is-active' : ''}`} onClick={() => setExportSettings({ scale: s })}>
              {s}×
            </button>
          ))}
        </div>
        <label className="opt-check">
          <input type="checkbox" checked={settings.background} onChange={(e) => setExportSettings({ background: e.target.checked })} />
          Board background (off = transparent)
        </label>
        <div className="insp-label">Padding</div>
        <div className="btn-row">
          {[0, 24, 64].map((p) => (
            <button key={p} type="button" className={`btn${settings.padding === p ? ' is-active' : ''}`} onClick={() => setExportSettings({ padding: p })}>
              {p}
            </button>
          ))}
        </div>
        <div className="insp-label">Export PNG</div>
        <div className="share-form">
          <button type="button" className="btn btn-primary" disabled={!!busy || selection.size === 0} onClick={() => run(selectionRect(), 'selection')}>
            Selection ({selection.size})
          </button>
          <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => run(boardBounds(board), 'board')}>
            Whole board
          </button>
          <button
            type="button"
            className="btn"
            disabled={!!busy}
            onClick={() => {
              pane.getState().setTool('exportArea');
              onClose();
            }}
          >
            Drag an area…
          </button>
        </div>
        <div className="insp-muted">Text items are rendered from their on-screen look; link-card images are left out.</div>

        <div className="insp-label">Board backup</div>
        <div className="share-form">
          <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => void runZip()}>
            ZIP with JSON + images
          </button>
          <button type="button" className="btn" disabled={!!busy} onClick={() => void runJson()}>
            JSON only
          </button>
        </div>
        {busy && <div className="insp-muted">{busy}</div>}
        {backupInfo && <div className="insp-muted">{backupInfo}</div>}
        {error && <div className="form-error">{error}</div>}
        <div className="insp-muted">
          board.json holds every item, layer, view and board setting (rich text as ProseMirror JSON); assets/ holds each image and attachment as stored.
          Files still uploading from another device are listed as missing.
        </div>
      </div>
    </PanelFrame>
  );
}
